import * as R from "ramda";

import { getLanguage } from "../utils/data/static/languages";
import { listHasItems } from "../utils/arrays";
import i18n from "../i18n";

const fieldsofstudy = [];

export const FORMS_KEYS = {
  details: [
    "title",
    "description",
    "positions",
    "jobType",
    "jobPayment",
    "salaryRange",
    "keywords",
    "team",
    "config",
    "notes",
    "showJobType",
    "showContractType",
    "private"
  ],
  media: ["video", "image"],
  candidate: [
    "professions",
    "nationalities",
    "country",
    "geolocations",
    "ageRange",
    "experienceRange",
    "englishTestRequired",
    "educationLevelsAdvanced",
    "universities",
    "skillsReq",
    "languages",
    "fieldsOfStudy"
  ],
  questions: ["questionsAdvanced"],
  interview: ["steps"]
};

export const PAGE_SIZE = 12;

export const CANDIDATE_LISTS = {
  suggested: "suggested",
  hunting: "hunting",
  selected: "selected",
  rejected: "rejected"
};
// TODO => Move to i18n
export const CANDIDATE_LISTS_URLS = {
  [CANDIDATE_LISTS.suggested]: "sugeridos",
  [CANDIDATE_LISTS.hunting]: "buscar",
  [CANDIDATE_LISTS.selected]: "seleccionados",
  [CANDIDATE_LISTS.rejected]: "rechazados"
};

const STRICT_FILTERS_OPTIONS = {
  agerange: "ageRange",
  languages: "languages",
  locationsgeometry: "geolocations",
  fieldsofstudy: "fieldsOfStudy",
  educationlevelsadvanced: "educationLevelsAdvanced"
};

const getOpportunityProp = (prop = "", pred = R.F, ifValid = R.empty) =>
  R.pipe(
    R.prop(prop),
    R.ifElse(pred, ifValid, R.F)
  );

const STRICT_FILTERS_ORDER = [
  STRICT_FILTERS_OPTIONS.agerange,
  STRICT_FILTERS_OPTIONS.educationlevelsadvanced,
  STRICT_FILTERS_OPTIONS.languages,
  STRICT_FILTERS_OPTIONS.fieldsofstudy,
  STRICT_FILTERS_OPTIONS.locationsgeometry
];

const separator = " - ";

const STRICT_FILTERS_LABELS = {
  [STRICT_FILTERS_OPTIONS.agerange]: ({ opportunity }) =>
    getOpportunityProp(
      STRICT_FILTERS_OPTIONS.agerange,
      listHasItems,
      R.ifElse(
        R.converge(R.equals, [R.head, R.last]),
        R.pipe(
          R.head,
          agerange => i18n.t("catalogs:agerange.singleAgeRange", { agerange })
        ),
        ([min, max]) => i18n.t("catalogs:agerange.multiAgeRange", { min, max })
      )
    )(opportunity),
  [STRICT_FILTERS_OPTIONS.educationlevelsadvanced]: ({ opportunity }) =>
    getOpportunityProp(
      STRICT_FILTERS_OPTIONS.educationlevelsadvanced,
      R.both(listHasItems, R.all(R.prop("level"))),
      R.compose(
        R.join(separator),
        R.map(({ level }) =>
          i18n.t(`catalogs:educationLevelsAdvanced.${level}`)
        )
      )
    )(opportunity),
  [STRICT_FILTERS_OPTIONS.languages]: ({ opportunity }) =>
    getOpportunityProp(
      STRICT_FILTERS_OPTIONS.languages,
      R.both(listHasItems, R.all(R.both(R.prop("name"), R.prop("level")))),
      R.pipe(
        R.map(
          ({ name, level }) =>
            `${getLanguage(name)} ${i18n.t(
              `catalogs:languages.levels.${level}`
            )}`
        ),
        R.join(separator)
      )
    )(opportunity),
  [STRICT_FILTERS_OPTIONS.fieldsofstudy]: ({ opportunity }) =>
    getOpportunityProp(
      STRICT_FILTERS_OPTIONS.fieldsofstudy,
      listHasItems,
      R.compose(
        R.join(separator),
        R.map(item => fieldsofstudy[item])
      )
    )(opportunity),
  [STRICT_FILTERS_OPTIONS.locationsgeometry]: ({ opportunity }) =>
    getOpportunityProp(
      STRICT_FILTERS_OPTIONS.locationsgeometry,
      R.both(listHasItems, R.all(R.prop("display_name"))),
      R.pipe(
        R.pluck("display_name"),
        // TODO revise: this takes only the city name
        R.map(city =>
          R.compose(
            R.nth(0),
            R.split(", ")
          )(city)
        ),
        R.join(separator)
      )
    )(opportunity)
};

export const STRICT_FILTERS = {
  labels: R.pick([STRICT_FILTERS_OPTIONS.languages], STRICT_FILTERS_LABELS),
  order: R.filter(
    R.equals(STRICT_FILTERS_OPTIONS.languages),
    STRICT_FILTERS_ORDER
  ),
  options: R.pick([STRICT_FILTERS_OPTIONS.languages], STRICT_FILTERS_OPTIONS)
};

export const FILTERS_FOR = {
  base: {
    referral: true,
    page: true,
    registered: true,
    workexperience: true,
    country: true,
    province: true,
    district: true,
    agerange: true,
    languages: false,
    education: true,
    fieldsofstudy: true,
    professions: true,
    skills: true
  },
  [CANDIDATE_LISTS.suggested]: {
    chatbotTags: true,
    tags: true,
    country: true,
    province: true,
    district: true,
    page: true,
    expectedsalary: true,
    referral: true,
    registered: true,
    strictFilters: true,
    workexperience: true,
    location: true,
    agerange: true,
    skills: true,
    languages: false,
    education: true,
    fieldsofstudy: true,
    professions: true,
    tagprofile: true,
    screeningstatus: true,
    score: true,
    sort: true,
    applydate: true,
    previousmatch: true
  },
  [CANDIDATE_LISTS.hunting]: {
    registered: true,
    country: true,
    province: true,
    district: true,
    page: true,
    workexperience: true,
    location: true,
    agerange: true,
    skills: true,
    languages: false,
    education: true,
    fieldsofstudy: true,
    professions: true,
    tagprofile: true,
    score: true,
    previousmatch: true
  },
  [CANDIDATE_LISTS.selected]: {
    registered: true
  },
  [CANDIDATE_LISTS.rejected]: {
    registered: true
  }
};

export const BATCH_ACTIONS_KEYS = {
  like: "like",
  superlike: "superlike",
  reject: "reject",
  // like and reject are descriptors for front-end
  likeExternal: "like-external",
  rejectExternal: "reject-external",
  moveExternal: "move-external", // this is the real action that works for external candidates
  moveToProcess: "move-to-process",
  moveToStep: "change-step",
  sendInvite: "send-invite",
  share: "invite-to-new-opp",
  markAsRead: "mark-as-read"
};

export const BATCH_ACTIONS = {
  // Indication of where the action comes from
  base: {
    share: BATCH_ACTIONS_KEYS.share
  },
  list: {
    moveToSelected: "move-to-selected",
    moveToProcess: "move-to-process",
    moveToRejected: "move-to-rejected",
    sendInvite: BATCH_ACTIONS_KEYS.sendInvite,
    share: BATCH_ACTIONS_KEYS.share,
    markAsRead: BATCH_ACTIONS_KEYS.markAsRead
  },
  process: {
    moveToStep: "move-to-step",
    moveToRejected: "move-to-rejected",
    sendInvite: BATCH_ACTIONS_KEYS.sendInvite
    // TODO enable in process?
    // ,share: BATCH_ACTIONS_KEYS.share
  }
};

export const CANDIDATE_TYPES = {
  internal: {
    type: "internal",
    completedScreening: {
      type: "completedScreening",
      status: ["passed", "completed"]
    },
    incompleteScreening: {
      type: "incompleteScreening",
      pendingScreening: {
        type: "pendingScreening",
        status: "pending"
      },
      disapprovedScreening: {
        type: "disapprovedScreening",
        status: "disapproved"
      }
    }
  },
  external: {
    type: "external"
  }
};

export const LIST_BATCH_ACTIONS_TRANSLATIONS = {
  [BATCH_ACTIONS.list.moveToSelected]: () =>
    i18n.t("opportunity:candidates.multipleSelection.selected"),
  [BATCH_ACTIONS.list.moveToProcess]: () =>
    i18n.t("opportunity:candidates.multipleSelection.process"),
  [BATCH_ACTIONS.list.moveToRejected]: () =>
    i18n.t("opportunity:candidates.multipleSelection.rejected"),
  [BATCH_ACTIONS.list.sendInvite]: () =>
    i18n.t("opportunity:candidates.multipleSelection.inviteCandidates"),
  [BATCH_ACTIONS.list.share]: () =>
    i18n.t("opportunity:candidates.multipleSelection.shareCandidateToOpp")
};

export const LIST_BATCH_ACTIONS = {
  [BATCH_ACTIONS.list.moveToProcess]: {
    [CANDIDATE_TYPES.external.type]: BATCH_ACTIONS_KEYS.likeExternal,
    [CANDIDATE_TYPES.internal.completedScreening.type]:
      BATCH_ACTIONS_KEYS.moveToProcess,
    [CANDIDATE_TYPES.internal.incompleteScreening.type]:
      BATCH_ACTIONS_KEYS.moveToProcess
  },
  [BATCH_ACTIONS.list.moveToRejected]: {
    [CANDIDATE_TYPES.external.type]: BATCH_ACTIONS_KEYS.rejectExternal,
    [CANDIDATE_TYPES.internal.completedScreening.type]:
      BATCH_ACTIONS_KEYS.reject,
    [CANDIDATE_TYPES.internal.incompleteScreening.type]:
      BATCH_ACTIONS_KEYS.reject
  },
  [BATCH_ACTIONS.list.moveToSelected]: {
    [CANDIDATE_TYPES.external.type]: null,
    [CANDIDATE_TYPES.internal.completedScreening.type]: BATCH_ACTIONS_KEYS.like,
    // There shouldn't be cases with incomplete screening in hunting, but just in case there is, this should catch those cases
    [CANDIDATE_TYPES.internal.incompleteScreening.type]: BATCH_ACTIONS_KEYS.like
  },
  [BATCH_ACTIONS.list.sendInvite]: {
    [CANDIDATE_TYPES.external.type]: BATCH_ACTIONS_KEYS.sendInvite,
    [CANDIDATE_TYPES.internal.completedScreening.type]: null,
    [CANDIDATE_TYPES.internal.incompleteScreening.type]: null
  },
  [BATCH_ACTIONS.list.share]: {
    [CANDIDATE_TYPES.internal.type]: BATCH_ACTIONS_KEYS.share
  }
};

export const PROCESS_BATCH_ACTIONS_TRANSLATIONS = {
  [BATCH_ACTIONS.process.moveToRejected]: () =>
    i18n.t("opportunity:candidates.multipleSelection.rejected"),
  [BATCH_ACTIONS.process.moveToStep]: translationOptions =>
    i18n.t(
      "opportunity:candidates.multipleSelection.originStep",
      translationOptions
    ),
  [BATCH_ACTIONS.process.sendInvite]: translationOptions =>
    i18n.t(
      "opportunity:candidates.multipleSelection.originStep",
      translationOptions
    )
};

export const PROCESS_BATCH_ACTIONS = {
  [BATCH_ACTIONS.process.moveToRejected]: {
    [CANDIDATE_TYPES.external.type]: BATCH_ACTIONS_KEYS.rejectExternal,
    [CANDIDATE_TYPES.internal.type]: BATCH_ACTIONS_KEYS.reject
  },
  [BATCH_ACTIONS.process.moveToStep]: {
    [CANDIDATE_TYPES.external.type]: BATCH_ACTIONS_KEYS.moveToStep,
    [CANDIDATE_TYPES.internal.type]: BATCH_ACTIONS_KEYS.moveToStep
  },
  [BATCH_ACTIONS.process.sendInvite]: {
    [CANDIDATE_TYPES.external.type]: BATCH_ACTIONS_KEYS.sendInvite,
    [CANDIDATE_TYPES.internal.type]: null
  }
};

export const DEFAULT_PROCESS_STEPS = {
  matches: "matches",
  selected: "selected",
  accepted: "accepted"
};

export const EDUCATION_LEVELS = [
  "secundario-avanzado",
  "secundario-completo",
  "terciario-avanzado",
  "terciario-completo",
  "universitario-avanzado",
  "universitario-completo",
  "postgrado-avanzado",
  "postgrado-completo",
  "master-avanzado",
  "master-completo",
  "doctorado-avanzado",
  "doctorado-completo",
  "intermedio-avanzado",
  "intermedio-completo",
  "primario-completo",
  "primario-avanzado"
];
